﻿(() => {
  'use strict';

  const config = window.KitchenCommerce || {};
  const routes = config.routes || {};
  const routeFromRoot = path => {
    const root = String(routes.root || '/');
    const normalizedRoot = root.endsWith('/') ? root : `${root}/`;
    return `${normalizedRoot}${String(path).replace(/^\//, '')}`;
  };
  const storefrontRoutes = {
    cart: routes.cart || routeFromRoot('cart'),
    cartAdd: routes.cartAdd || routeFromRoot('cart/add'),
    cartChange: routes.cartChange || routeFromRoot('cart/change'),
    checkout: routes.checkout || routeFromRoot('checkout'),
    allProducts: routes.allProducts || routeFromRoot('collections/all')
  };
  const drawer = document.querySelector('[data-cart-drawer]');
  const quickView = document.querySelector('[data-quick-view-dialog]');
  let lastFocusedElement = null;
  let lastLightboxFocusedElement = null;
  let lastQuickViewFocusedElement = null;

  const escapeHtml = value => String(value || '').replace(/[&<>'"]/g, character => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' }[character]));
  const imageWithWidth = (url, width) => url ? `${url}${String(url).includes('?') ? '&' : '?'}width=${width}` : '';
  const predictiveImageUrl = product => product?.featured_image?.url || product?.image?.url || product?.image || '';
  const endpoint = route => `${String(route).replace(/\/$/, '')}.js`;
  const formatMoney = (cents, currency) => new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currency || window.Shopify?.currency?.active || document.documentElement.dataset.currency || 'USD'
  }).format((Number(cents) || 0) / 100);
  const formatSearchPrice = value => Number.isFinite(Number(value)) && String(value).trim() !== '' ? formatMoney(value) : String(value || '');

  const setCartCount = count => document.querySelectorAll('[data-cart-count]').forEach(node => {
    node.textContent = count;
    node.hidden = !count;
  });

  const getCart = () => fetch(endpoint(storefrontRoutes.cart), { headers: { Accept: 'application/json' } })
    .then(response => response.ok ? response.json() : Promise.reject(response));

  const renderDrawer = cart => {
    const content = drawer?.querySelector('[data-cart-content]');
    const footer = drawer?.querySelector('[data-cart-footer]');
    document.querySelectorAll('[data-drawer-count]').forEach(node => {
      node.textContent = `${cart.item_count} ${cart.item_count === 1 ? 'item' : 'items'}`;
    });
    if (!content || !footer) return;

    if (!cart.item_count) {
      content.innerHTML = `<div class="drawer-empty"><p>Your cart is empty. Start with the products you will use most.</p><a class="btn" href="${escapeHtml(storefrontRoutes.allProducts)}" data-cart-close>Browse the catalog</a></div>`;
      footer.innerHTML = '';
      return;
    }

    content.innerHTML = cart.items.map(item => `
      <article class="drawer-line">
        <a href="${escapeHtml(item.url)}">${item.image ? `<img src="${escapeHtml(item.image)}" alt="${escapeHtml(item.title)}">` : ''}</a>
        <div>
          <h3><a href="${escapeHtml(item.url)}">${escapeHtml(item.product_title || item.title)}</a></h3>
          <p>${escapeHtml(item.variant_title || 'Default title')}</p>
          <div class="drawer-line__bottom">
            <div class="drawer-qty">
              <button type="button" data-cart-change="${escapeHtml(item.key)}" data-cart-quantity="${item.quantity - 1}" aria-label="Decrease quantity">&minus;</button>
              <span>${item.quantity}</span>
              <button type="button" data-cart-change="${escapeHtml(item.key)}" data-cart-quantity="${item.quantity + 1}" aria-label="Increase quantity">+</button>
            </div>
            <div class="drawer-line__price">${formatMoney(item.final_line_price, cart.currency)}<button class="drawer-remove" type="button" data-cart-change="${escapeHtml(item.key)}" data-cart-quantity="0">Remove</button></div>
          </div>
        </div>
      </article>`).join('');

    footer.innerHTML = `<div class="drawer-total"><span>Subtotal</span><b>${formatMoney(cart.total_price, cart.currency)}</b></div><p>Taxes are calculated at checkout.</p><a class="btn" href="${escapeHtml(storefrontRoutes.checkout)}">Continue to checkout</a><p>Payment is handled securely at checkout.</p>`;
  };

  const refreshCart = () => getCart()
    .then(cart => {
      setCartCount(cart.item_count);
      renderDrawer(cart);
      return cart;
    })
    .catch(() => null);

  const closeCart = () => {
    if (!drawer) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    drawer.hidden = true;
    document.documentElement.style.overflow = '';
    if (lastFocusedElement instanceof HTMLElement) lastFocusedElement.focus();
  };

  const openCart = () => {
    if (!drawer) return;
    lastFocusedElement = document.activeElement;
    drawer.hidden = false;
    window.requestAnimationFrame(() => drawer.classList.add('is-open'));
    drawer.setAttribute('aria-hidden', 'false');
    document.documentElement.style.overflow = 'hidden';
    refreshCart().finally(() => setTimeout(() => drawer.querySelector('[data-cart-close]')?.focus(), 0));
  };

  const changeCart = (id, quantity) => fetch(endpoint(storefrontRoutes.cartChange), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ id, quantity: Math.max(0, Number(quantity) || 0) })
  })
    .then(response => response.ok ? response.json() : Promise.reject(response))
    .then(cart => {
      setCartCount(cart.item_count);
      renderDrawer(cart);
    });

  const closeLightbox = () => {
    const lightbox = document.querySelector('[data-lightbox]');
    lightbox?.classList.remove('is-open');
    lightbox?.setAttribute('aria-hidden', 'true');
    if (lightbox) lightbox.hidden = true;
    document.documentElement.style.overflow = '';
    if (lastLightboxFocusedElement instanceof HTMLElement) lastLightboxFocusedElement.focus();
  };

  const closeQuickView = () => {
    if (!quickView) return;
    quickView.hidden = true;
    quickView.setAttribute('aria-hidden', 'true');
    document.documentElement.style.overflow = '';
    if (lastQuickViewFocusedElement instanceof HTMLElement) lastQuickViewFocusedElement.focus();
  };

  const updateQuickViewVariant = variant => {
    if (!quickView || !variant) return;
    const price = quickView.querySelector('[data-quick-view-price]');
    const button = quickView.querySelector('[data-add-to-cart]');
    if (price) price.textContent = formatMoney(variant.price);
    if (button) {
      button.disabled = !variant.available;
      button.textContent = variant.available ? 'Add to cart' : 'Sold out';
    }
    const image = quickView.querySelector('[data-quick-view-image]');
    const variantImage = variant.featured_image?.src || variant.featured_image;
    if (image && variantImage) image.src = variantImage;
  };

  const openQuickView = (productUrl, trigger) => {
    if (!quickView || !productUrl) return;
    lastQuickViewFocusedElement = trigger || document.activeElement;
    const panel = quickView.querySelector('.quick-view__panel');
    const title = quickView.querySelector('[data-quick-view-title]');
    const vendor = quickView.querySelector('[data-quick-view-vendor]');
    const description = quickView.querySelector('[data-quick-view-description]');
    const image = quickView.querySelector('[data-quick-view-image]');
    const select = quickView.querySelector('[data-quick-view-variants]');
    const link = quickView.querySelector('[data-quick-view-link]');
    if (title) title.textContent = 'Loading product…';
    if (description) description.textContent = '';
    if (image) { image.removeAttribute('src'); image.alt = ''; }
    if (select) select.innerHTML = '';
    quickView.hidden = false;
    quickView.setAttribute('aria-hidden', 'false');
    document.documentElement.style.overflow = 'hidden';
    panel?.focus();

    const jsonUrl = `${String(productUrl).split('?')[0].replace(/\.js$/, '')}.js`;
    fetch(jsonUrl, { headers: { Accept: 'application/json' } })
      .then(response => response.ok ? response.json() : Promise.reject(response))
      .then(product => {
        if (title) title.textContent = product.title || 'Product';
        if (vendor) vendor.textContent = product.vendor || 'Featured product';
        if (description) {
          const parsed = new DOMParser().parseFromString(product.description || '', 'text/html');
          description.textContent = parsed.body.textContent?.trim() || '';
        }
        if (image) {
          image.src = product.featured_image || product.images?.[0] || '';
          image.alt = product.title || '';
        }
        if (link) link.href = productUrl;
        if (select) {
          select.innerHTML = (product.variants || []).map(variant => `<option value="${escapeHtml(variant.id)}" data-price="${escapeHtml(variant.price)}" ${variant.available ? '' : 'disabled'}>${escapeHtml(variant.public_title || variant.title || 'Default')} ${variant.available ? '' : '— Sold out'}</option>`).join('');
          const firstAvailable = (product.variants || []).find(variant => variant.available) || product.variants?.[0];
          if (firstAvailable) {
            select.value = String(firstAvailable.id);
            select.dataset.variants = JSON.stringify(product.variants || []);
            updateQuickViewVariant(firstAvailable);
          }
        }
      })
      .catch(() => {
        if (title) title.textContent = 'Preview unavailable';
        if (description) description.textContent = 'Open the product page to view its complete details.';
        if (link) link.href = productUrl;
      });
  };

  const getProductVariants = form => {
    const source = form?.querySelector('[data-product-variants]');
    if (!source) return [];
    try {
      const variants = JSON.parse(source.textContent);
      return Array.isArray(variants) ? variants : [];
    } catch {
      return [];
    }
  };

  const getSelectedVariant = (form, variants = getProductVariants(form)) => {
    if (!variants.length) return null;
    const values = [...form.querySelectorAll('[data-option-input]:checked')].map(input => input.value);
    if (values.length) {
      return variants.find(item => Array.isArray(item.options)
        && item.options.length === values.length
        && item.options.every((value, index) => value === values[index])) || null;
    }
    const select = form.querySelector('[name="id"]');
    return variants.find(item => String(item.id) === String(select?.value)) || null;
  };

  const syncPurchaseControls = (form, variant) => {
    const available = Boolean(variant?.available);
    const buybox = form.closest('.buybox');
    const add = form.querySelector('[data-add-to-cart]');
    if (add) {
      add.disabled = !available;
      add.textContent = available ? 'Add to cart' : (variant ? 'Sold out' : 'Unavailable');
    }
    const whop = form.querySelector('[data-whop-buy]');
    if (whop) {
      whop.disabled = !available;
      whop.hidden = !available;
      whop.setAttribute('aria-hidden', String(!available));
    }
    const nativePayment = form.querySelector('[data-native-payment]');
    if (nativePayment) {
      nativePayment.hidden = !available;
      nativePayment.setAttribute('aria-hidden', String(!available));
    }
    const buyNow = form.querySelector('[data-buy-now]');
    if (buyNow) {
      buyNow.disabled = !available;
      buyNow.hidden = !available;
      buyNow.setAttribute('aria-hidden', String(!available));
    }
    buybox?.querySelectorAll('[data-product-availability]').forEach(node => { node.hidden = !available; });
    buybox?.querySelectorAll('[data-product-availability-text]').forEach(node => { node.textContent = available ? 'Available' : 'Unavailable'; });
    buybox?.querySelectorAll('[data-low-stock]').forEach(node => {
      const threshold = Number(node.dataset.lowStockThreshold || 0);
      const quantity = Number(variant?.inventory_quantity);
      const show = available && Number.isFinite(quantity) && quantity > 0 && quantity <= threshold;
      node.hidden = !show;
      const text = node.querySelector('[data-low-stock-text]');
      if (show && text) text.textContent = `Only ${quantity} left in stock`;
    });
  };

  const syncVariant = form => {
    const variants = getProductVariants(form);
    const select = form.querySelector('[name="id"]');
    if (!variants.length || !select) return;

    const variant = getSelectedVariant(form, variants);
    if (!variant) {
      syncPurchaseControls(form, null);
      return;
    }

    select.value = variant.id;
    const buybox = form.closest('.buybox');
    const price = buybox?.querySelector('[data-product-price]');
    const compare = buybox?.querySelector('[data-product-compare]');
    const save = buybox?.querySelector('[data-product-save]');
    const isOnSale = variant.compare_at_price && variant.compare_at_price > variant.price;
    if (price) price.textContent = formatMoney(variant.price);
    if (compare) {
      compare.hidden = !isOnSale;
      if (isOnSale) compare.textContent = formatMoney(variant.compare_at_price);
    }
    if (save) {
      save.hidden = !isOnSale;
      if (isOnSale) save.textContent = `Save ${formatMoney(variant.compare_at_price - variant.price)}`;
    }
    buybox?.querySelectorAll('[data-product-sale]').forEach(node => { node.hidden = !isOnSale; });
    syncPurchaseControls(form, variant);
    document.querySelectorAll('[data-sticky-product-price]').forEach(node => { node.textContent = formatMoney(variant.price); });
  };

  const loadProductRecommendations = () => {
    document.querySelectorAll('[data-recommendations-section][data-recommendations-url]').forEach(container => {
      const url = container.dataset.recommendationsUrl;
      if (!url || container.dataset.recommendationsLoaded) return;
      container.dataset.recommendationsLoaded = 'true';
      fetch(url, { headers: { Accept: 'text/html' } })
        .then(response => response.ok ? response.text() : Promise.reject(response))
        .then(markup => {
          const documentFragment = new DOMParser().parseFromString(markup, 'text/html');
          const replacement = documentFragment.querySelector('[data-recommendations-section]');
          if (replacement && !replacement.hidden) container.replaceWith(replacement);
        })
        .catch(() => {});
    });
  };

  loadProductRecommendations();

  const predictiveEndpoint = `${String(routes.predictiveSearch || routeFromRoot('search/suggest')).replace(/\.json$/, '')}.json`;
  if (config.predictiveSearchEnabled !== false) document.querySelectorAll('[data-predictive-search]').forEach(form => {
    const input = form.querySelector('input[name="q"]');
    const results = form.querySelector('[data-predictive-results]');
    let timer = null;
    let requestId = 0;
    if (!input || !results) return;
    input.addEventListener('input', () => {
      window.clearTimeout(timer);
      const query = input.value.trim();
      if (query.length < 2) { results.hidden = true; results.innerHTML = ''; return; }
      timer = window.setTimeout(() => {
        const currentRequest = ++requestId;
        const url = new URL(predictiveEndpoint, window.location.origin);
        url.searchParams.set('q', query);
        url.searchParams.set('resources[type]', 'product');
        url.searchParams.set('resources[limit]', '5');
        fetch(url, { headers: { Accept: 'application/json' } })
          .then(response => response.ok ? response.json() : Promise.reject(response))
          .then(payload => {
            if (currentRequest !== requestId) return;
            const products = payload?.resources?.results?.products || [];
            results.innerHTML = products.length
              ? `<p class="predictive-search__title">Suggested products</p>${products.map(product => `<a class="predictive-search__item" role="option" href="${escapeHtml(product.url)}">${predictiveImageUrl(product) ? `<img src="${escapeHtml(imageWithWidth(predictiveImageUrl(product), 108))}" alt="">` : '<span></span>'}<b>${escapeHtml(product.title)}</b><small>${escapeHtml(formatSearchPrice(product.price))}</small></a>`).join('')}<a class="predictive-search__all" href="${escapeHtml(`${routes.root || '/'}search?q=${encodeURIComponent(query)}`)}">See all search results <span>→</span></a>`
              : '<p class="predictive-search__empty">No matching products yet.</p>';
            results.hidden = false;
          })
          .catch(() => { results.hidden = true; });
      }, 180);
    });
    input.addEventListener('focus', () => { if (results.innerHTML) results.hidden = false; });
  });

  const revealItems = root => {
    const items = [...root.querySelectorAll('main > section, .merch-collection-card, .premium-catalog-card, .home-assurance__item, .editorial-showcase__points > div')];
    if (config.motionEnabled === false || !('IntersectionObserver' in window) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    document.documentElement.classList.add('motion-ready');
    const observer = new IntersectionObserver(entries => entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('is-visible');
      observer.unobserve(entry.target);
    }), { rootMargin: '0px 0px -7% 0px', threshold: .08 });
    items.forEach((item, index) => {
      item.dataset.reveal = '';
      item.style.transitionDelay = `${Math.min(index % 4, 3) * 55}ms`;
      observer.observe(item);
    });
  };
  revealItems(document);

  const stickyPurchaseBar = document.querySelector('[data-product-sticky]');
  const productPurchaseForm = document.querySelector('.premium-product-form');
  if (stickyPurchaseBar && productPurchaseForm) {
    let stickyUpdateQueued = false;
    const updateStickyPurchaseBar = () => {
      const formPosition = productPurchaseForm.getBoundingClientRect();
      const visible = formPosition.bottom < 96;
      stickyPurchaseBar.classList.toggle('is-visible', visible);
      stickyPurchaseBar.setAttribute('aria-hidden', String(!visible));
      stickyUpdateQueued = false;
    };
    const queueStickyPurchaseUpdate = () => {
      if (stickyUpdateQueued) return;
      stickyUpdateQueued = true;
      window.requestAnimationFrame(updateStickyPurchaseBar);
    };
    updateStickyPurchaseBar();
    window.addEventListener('scroll', queueStickyPurchaseUpdate, { passive: true });
    window.addEventListener('resize', queueStickyPurchaseUpdate);
    document.addEventListener('shopify:section:load', queueStickyPurchaseUpdate);
  }

  document.addEventListener('click', event => {
    const quickViewTrigger = event.target.closest('[data-quick-view]');
    if (quickViewTrigger) { openQuickView(quickViewTrigger.dataset.productUrl, quickViewTrigger); return; }

    if (event.target.closest('[data-quick-view-close]')) { closeQuickView(); return; }

    const cartToggle = event.target.closest('[data-cart-toggle]');
    if (cartToggle) { openCart(); return; }

    const cartClose = event.target.closest('[data-cart-close]');
    if (cartClose) { closeCart(); return; }

    const cartChange = event.target.closest('[data-cart-change]');
    if (cartChange) { changeCart(cartChange.dataset.cartChange, cartChange.dataset.cartQuantity).catch(() => refreshCart()); return; }

    const cartPageChange = event.target.closest('[data-cart-page-change]');
    if (cartPageChange) {
      const quantityInput = cartPageChange.parentElement?.querySelector('[data-cart-page-quantity]');
      const quantity = Math.max(0, (Number(quantityInput?.value) || 0) + Number(cartPageChange.dataset.cartPageDirection || 0));
      if (quantityInput) quantityInput.value = quantity;
      changeCart(cartPageChange.dataset.cartPageChange, quantity)
        .then(() => window.location.reload())
        .catch(() => cartPageChange.closest('form')?.requestSubmit());
      return;
    }

    const shopToggle = event.target.closest('[data-shop-toggle]');
    if (shopToggle) {
      const panel = document.querySelector('[data-shop-dropdown]');
      const open = !panel?.classList.contains('is-open');
      panel?.classList.toggle('is-open', open);
      shopToggle.setAttribute('aria-expanded', String(open));
      return;
    }

    const mobileToggle = event.target.closest('[data-mobile-toggle]');
    if (mobileToggle) {
      const nav = document.querySelector('[data-mobile-nav]');
      const open = Boolean(nav?.hidden);
      if (nav) nav.hidden = !open;
      mobileToggle.setAttribute('aria-expanded', String(open));
      return;
    }

    const addressEdit = event.target.closest('[data-address-edit]');
    if (addressEdit) {
      const editor = document.getElementById(`AddressEdit-${addressEdit.dataset.addressEdit}`);
      if (editor) editor.hidden = !editor.hidden;
      addressEdit.setAttribute('aria-expanded', String(editor && !editor.hidden));
      return;
    }

    const quantityMinus = event.target.closest('[data-quantity-minus]');
    const quantityPlus = event.target.closest('[data-quantity-plus]');
    if (quantityMinus || quantityPlus) {
      const control = (quantityMinus || quantityPlus).closest('.premium-quantity-control');
      const input = control?.querySelector('input[name="quantity"]');
      if (input) {
        const current = Math.max(1, Number(input.value) || 1);
        input.value = String(quantityMinus ? Math.max(1, current - 1) : Math.min(Number(input.max) || 99, current + 1));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
      return;
    }

    const stickyBuy = event.target.closest('[data-sticky-buy]');
    if (stickyBuy) {
      // Conversion best practice: the sticky bar adds the current variant
      // directly instead of merely scrolling. Falls back to scrolling when
      // the variant is unavailable.
      const stickyForm = document.querySelector('.premium-product-form');
      const stickyAdd = stickyForm && stickyForm.querySelector('[data-add-to-cart]');
      if (stickyAdd && !stickyAdd.disabled) { stickyAdd.click(); }
      else if (stickyForm && stickyForm.scrollIntoView) { stickyForm.scrollIntoView({ behavior: 'smooth', block: 'center' }); }
      return;
    }

    const thumbnail = event.target.closest('.gallery__thumbs button');
    if (thumbnail) {
      const image = thumbnail.querySelector('[data-gallery-image]');
      const main = document.querySelector('[data-gallery-main]');
      if (image && main) {
        main.src = image.dataset.galleryImage;
        // The main image carries a srcset: without clearing it the browser
        // keeps the previously selected candidate and the swap never shows.
        main.removeAttribute('srcset');
        main.removeAttribute('sizes');
        main.alt = image.alt || main.alt;
        thumbnail.closest('.gallery__thumbs').querySelectorAll('button').forEach(button => button.removeAttribute('aria-current'));
        thumbnail.setAttribute('aria-current', 'true');
      }
      return;
    }

    if (event.target.closest('[data-gallery-open]')) {
      const main = document.querySelector('[data-gallery-main]');
      const lightbox = document.querySelector('[data-lightbox]');
      const image = lightbox?.querySelector('[data-lightbox-image]');
      if (main && lightbox && image) {
        lastLightboxFocusedElement = document.activeElement;
        image.src = main.src;
        image.alt = main.alt;
        lightbox.hidden = false;
        lightbox.classList.add('is-open');
        lightbox.setAttribute('aria-hidden', 'false');
        document.documentElement.style.overflow = 'hidden';
        lightbox.querySelector('[data-lightbox-close]')?.focus();
      }
      return;
    }

    if (event.target.closest('[data-lightbox-close]') || event.target.matches('[data-lightbox]')) {
      closeLightbox();
      return;
    }

    const whop = event.target.closest('[data-whop-buy]');
    if (!whop) return;
    event.preventDefault();
    if (whop.disabled || !config.whopEnabled || !config.whopWorkerUrl || !config.shopDomain) return;

    const form = whop.closest('form');
    const variant = form ? getSelectedVariant(form) : null;
    if (!variant || !variant.available) {
      if (form) syncVariant(form);
      return;
    }
    const quantity = form?.querySelector('[name="quantity"]');
    const original = whop.textContent;
    whop.disabled = true;
    whop.textContent = 'Preparing secure checkout...';
    fetch(`${config.whopWorkerUrl.replace(/\/$/, '')}/api/buy-now`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        shop_domain: config.shopDomain,
        variant_id: variant.id,
        handle: whop.dataset.handle,
        quantity: Math.max(1, Number(quantity?.value) || 1)
      })
    })
      .then(response => response.json().then(data => ({ response, data })))
      .then(result => {
        if (!result.response.ok || !result.data.ok || !result.data.checkout_url) throw new Error(result.data.error || 'Could not begin checkout.');
        window.location.assign(result.data.checkout_url);
      })
      .catch(error => {
        whop.disabled = false;
        whop.textContent = original;
        window.alert(error.message || 'Checkout could not be started.');
      });
  });

  document.addEventListener('change', event => {
    const quickViewSelect = event.target.closest('[data-quick-view-variants]');
    if (quickViewSelect) {
      try {
        const variants = JSON.parse(quickViewSelect.dataset.variants || '[]');
        updateQuickViewVariant(variants.find(variant => String(variant.id) === String(quickViewSelect.value)));
      } catch { /* Keep the initial variant if product data is unavailable. */ }
    }

    const option = event.target.closest('[data-option-input]');
    if (option) syncVariant(option.closest('form'));

    const cartPageQuantity = event.target.closest('[data-cart-page-quantity]');
    if (cartPageQuantity) {
      const quantity = Math.max(0, Number(cartPageQuantity.value) || 0);
      changeCart(cartPageQuantity.dataset.cartPageKey, quantity)
        .then(() => window.location.reload())
        .catch(() => cartPageQuantity.closest('form')?.requestSubmit());
    }
  });

  document.addEventListener('submit', event => {
    const form = event.target.closest('[data-ajax-cart]');
    if (!form) return;

    const addToCart = form.querySelector('[data-add-to-cart]');
    if (event.submitter && event.submitter !== addToCart) return;

    if (form.querySelector('[data-product-variants]')) {
      const variant = getSelectedVariant(form);
      if (!variant || !variant.available) {
        event.preventDefault();
        syncVariant(form);
        return;
      }
    }

    event.preventDefault();
    const button = addToCart || form.querySelector('[type="submit"]');
    const original = button?.textContent;
    if (button) {
      button.disabled = true;
      button.textContent = 'Adding...';
    }
    fetch(endpoint(storefrontRoutes.cartAdd), {
      method: 'POST',
      body: new FormData(form),
      headers: { Accept: 'application/json' }
    })
      .then(response => response.ok ? response.json() : Promise.reject(response))
      .then(() => {
        if (button) button.textContent = 'Added';
        closeQuickView();
        openCart();
        setTimeout(() => {
          if (button) {
            button.disabled = false;
            button.textContent = original;
          }
        }, 1200);
      })
      .catch(() => {
        if (button) {
          button.disabled = false;
          button.textContent = original;
        }
        form.submit();
      });
  });

  document.addEventListener('keydown', event => {
    if (event.key === 'Escape') {
      closeCart();
      closeLightbox();
      closeQuickView();
      const panel = document.querySelector('[data-shop-dropdown]');
      panel?.classList.remove('is-open');
      document.querySelector('[data-shop-toggle]')?.setAttribute('aria-expanded', 'false');
      const mobileNav = document.querySelector('[data-mobile-nav]');
      if (mobileNav) mobileNav.hidden = true;
      document.querySelector('[data-mobile-toggle]')?.setAttribute('aria-expanded', 'false');
    }

    const activeDialog = drawer?.classList.contains('is-open') ? drawer : (!quickView?.hidden ? quickView : document.querySelector('[data-lightbox].is-open'));
    if (event.key !== 'Tab' || !activeDialog) return;
    const focusable = [...activeDialog.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')]
      .filter(node => !node.hidden);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  document.addEventListener('click', event => {
    const menu = document.querySelector('.shop-menu');
    if (menu && !menu.contains(event.target)) {
      document.querySelector('[data-shop-dropdown]')?.classList.remove('is-open');
      document.querySelector('[data-shop-toggle]')?.setAttribute('aria-expanded', 'false');
    }
    document.querySelectorAll('[data-predictive-search]').forEach(form => {
      if (!form.contains(event.target)) {
        const results = form.querySelector('[data-predictive-results]');
        if (results) results.hidden = true;
      }
    });
  });

  document.querySelectorAll('[data-product-variants]').forEach(source => {
    const form = source.closest('form');
    if (form) syncVariant(form);
  });

  refreshCart();
})();
